import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Mail, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FieldError } from "@/components/form/FieldError";
import { SubmitButton } from "@/components/form/SubmitButton";
import { authApi } from "@/features/auth/api";
import { OtpCodeStep } from "@/components/auth/OtpCodeStep";
import { formatApiError } from "@/features/auth/formatError";
import { ROUTES } from "@/constants/routes";

const schema = z.object({
    email: z
        .string()
        .trim()
        .min(1, "Email is required")
        .email("Enter a valid email"),
});

type FormValues = z.infer<typeof schema>;

export default function ForgotPassword() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState<string | null>(null);

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<FormValues>({
        resolver: zodResolver(schema),
        defaultValues: { email: "" },
        mode: "onBlur",
    });

    const onSubmit = async ({ email }: FormValues) => {
        setLoading(true);
        try {
            await authApi.sendOtp(email.toLowerCase(), "reset");
            setSent(email.toLowerCase());
            toast.success("Code sent", {
                description: `If an account exists for ${email}, we've emailed a 6-digit code.`,
            });
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    const onVerify = async (code: string) => {
        if (!sent) return;
        setLoading(true);
        try {
            const { token } = await authApi.verifyResetOtp(sent, code);
            navigate(`${ROUTES.resetPassword}?token=${encodeURIComponent(token)}`, { replace: true });
        } catch (err) {
            toast.error(formatApiError(err, "Verification failed"));
        } finally {
            setLoading(false);
        }
    };

    if (sent) {
        return (
            <div data-testid="forgot-otp-step" className="space-y-6">
                <div>
                    <h1 className="text-3xl font-extrabold tracking-tight text-foreground">
                        Check your email
                    </h1>
                </div>
                <OtpCodeStep
                    email={sent}
                    purpose="reset"
                    loading={loading}
                    onVerify={onVerify}
                    onChangeEmail={() => setSent(null)}
                    testIdPrefix="forgot"
                />
            </div>
        );
    }

    return (
        <div data-testid="forgot-page" className="space-y-8">
            <header>
                <Link
                    to={ROUTES.login}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-brand"
                    data-testid="forgot-back-link"
                >
                    <ArrowLeft className="h-3.5 w-3.5" />
                    Back to sign in
                </Link>
                <h1 className="mt-4 text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
                    Forgot your password?
                </h1>
                <p className="mt-2 text-sm text-muted-foreground">
                    Enter the email tied to your TSGo account and we&apos;ll email you a
                    6-digit code to reset it.
                </p>
            </header>

            <form
                noValidate
                onSubmit={handleSubmit(onSubmit)}
                aria-label="Forgot password form"
                className="space-y-5"
            >
                <div className="space-y-1.5">
                    <Label htmlFor="forgot-email">Email</Label>
                    <div className="relative">
                        <Mail className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            id="forgot-email"
                            type="email"
                            inputMode="email"
                            autoComplete="email"
                            placeholder="you@example.com"
                            className="h-11 pl-9"
                            data-testid="forgot-email"
                            aria-invalid={!!errors.email || undefined}
                            aria-describedby={
                                errors.email ? "forgot-email-error" : undefined
                            }
                            disabled={loading}
                            {...register("email")}
                        />
                    </div>
                    <FieldError
                        id="forgot-email-error"
                        message={errors.email?.message}
                    />
                </div>

                <SubmitButton
                    type="submit"
                    loading={loading}
                    loadingText="Sending code…"
                    data-testid="forgot-submit"
                >
                    Send code
                </SubmitButton>
            </form>
        </div>
    );
}
